import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, Loader2, ArrowLeft, Briefcase, Building2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { markPromotedToAdmin } from "@/lib/rolePromotion";
import {
  orgAdminClaimErrorMessage,
  pendingInviteRedirect,
  readMemberInviteToken,
  resolveSignupContinuation,
  signupEmailRedirectUrl,
} from "@/lib/orgInvitations";
import { clearCapturedOrgInvite, readCapturedOrgInvite } from "@/lib/orgInviteBootstrap";
import {
  reviewResetPasswordUrl,
  reviewSignupRedirectUrl,
} from "@/lib/reviewInvitations";

import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import PasswordStrengthMeter, { getPasswordError } from "@/components/auth/PasswordStrengthMeter";
import { ADMIN_HOME, resolvePostLoginDestination } from "@/lib/postLoginDestination";
import { buildSignupMetadata } from "@/lib/signupMetadata";
import type { SignupAccountType } from "@/lib/signupMetadata";
import { claimSignupRedirectUrl, validClaimReturn, type ClaimReturn } from "@/lib/orgClaimReturn";
import { SignupNameFields } from "@/components/auth/SignupNameFields";
import { WaitlistDialog } from "@/components/waitlist";
import MarketingDialog from "@/components/marketing/MarketingDialog";
import { GoogleIcon as MarketingGoogleIcon } from "@/components/marketing/icons";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: "signin" | "signup" | "forgot";
  allowSignup?: boolean;
  /**
   * Run INSTEAD of the role-resolved navigation once password authentication
   * succeeds, so a caller that must keep the visitor on its own page can.
   *
   * Only the role-destination navigation is replaced. The organization-admin
   * and member-invitation continuations keep their own terminal routing
   * untouched, because those are different invitations with their own
   * destinations and are never issued to a review recipient.
   *
   * Every caller that does not pass this keeps its current behaviour exactly.
   */
  onAuthenticated?: () => void;
  /**
   * A review invitation token to carry through an email round trip. The token
   * is appended to the constant /auth/callback and /reset-password paths and
   * to the OAuth intent blob — the same opaque-token convention the member
   * invitation already uses. No destination URL is ever accepted.
   */
  reviewToken?: string | null;
  /**
   * "marketing" renders the sign-in, forgot-password and reset-sent views in
   * the public marketing site's look, with errors shown inline instead of as a
   * toast. Behaviour is unchanged: the same handlers, OAuth intent, recovery
   * redirect and role-resolved destination. Sign-up, invitation and
   * email-confirmation views always use the default look.
   */
  appearance?: "default" | "marketing";
  /**
   * One line under the sign-up title saying what the account unlocks (a
   * signUpPromptCopy entry). Display only; sign-up itself is unchanged.
   */
  signUpIntro?: string;
  /**
   * "agent" hides the sign-up account-type selector and keeps sign-up on an
   * ordinary (agent) account. Organization sign-up is invite-only, so a caller
   * whose flow never creates an organization account (the listing claim page)
   * must not offer a choice that dead-ends. An org-admin invitation captured
   * in memory still takes its own path. Every other caller is unchanged.
   */
  lockAccountType?: "agent";
  /**
   * The listing claim to return to after authentication
   * (/claim-organization/:slug). Carried through Google OAuth in the stored
   * intent and through email confirmation on the constant /auth/callback URL;
   * /auth/callback rebuilds the path from the validated slug only
   * (src/lib/orgClaimReturn.ts). Password sign-in and an autoconfirmed sign-up
   * return through `onAuthenticated` instead.
   */
  claimReturn?: ClaimReturn | null;
}

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const AuthModal = ({
  isOpen,
  onClose,
  defaultMode = "signin",
  allowSignup = true,
  onAuthenticated,
  reviewToken,
  appearance = "default",
  signUpIntro,
  lockAccountType,
  claimReturn,
}: AuthModalProps) => {
  // Re-validated here so an invalid value is never stored or sent.
  const claim = validClaimReturn(claimReturn);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">(
    allowSignup ? defaultMode : "signin"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  // The organization's name for an org sign-up. Prefilled from the invite
  // preview; read-only for a bound invitation. Never copied into fullName.
  const [organizationName, setOrganizationName] = useState("");
  const [accountType, setAccountType] = useState<SignupAccountType>("agent");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  // Set when signUp returns no session because email confirmation is required.
  // Holds the address the confirmation link went to; the flag records whether a
  // member invitation is waiting on that confirmation, which only changes copy.
  const [confirmationSentTo, setConfirmationSentTo] = useState<string | null>(null);
  const [confirmationIsInvite, setConfirmationIsInvite] = useState(false);
  // Set when an ORG-ADMIN invitation is pending email confirmation: the credential
  // is deliberately not carried through the confirmation redirect (see
  // SignupContinuation in orgInvitations.ts), so the card tells the recipient to
  // reopen their invitation link — still valid — once confirmed.
  const [confirmationOrgName, setConfirmationOrgName] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [authMethod, setAuthMethod] = useState<"google" | "email">("email");
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  // Marketing look only: the runtime error message, shown inline in the dialog.
  const [inlineError, setInlineError] = useState<string | null>(null);
  // Closed-beta org-admin invite (the #org_invite= fragment, lifted into memory
  // by src/lib/orgInviteBootstrap.ts before anything else runs): when valid,
  // signup is forced and the token is the ONLY way to obtain admin. The invited email is
  // NOT returned by get_org_invite anymore (it must not be disclosed to an
  // anonymous token holder), so the recipient types their own email and the
  // server-side claim validates the match. `isBound` marks a pre-activation
  // invitation whose organization already exists.
  const [orgInvite, setOrgInvite] = useState<{
    token: string;
    orgName: string | null;
    isBound: boolean;
    logoUrl: string | null;
  } | null>(null);
  const [orgInviteError, setOrgInviteError] = useState<string | null>(null);
  const { toast } = useToast();

  // A member invitation (?invite_token=) already decides that the signer-up is
  // organization staff, so signup must NOT re-ask account type: the selector is
  // hidden, mirroring how a valid org-admin invite (orgInvite) locks the
  // account type. The two links are never issued to the same recipient.
  //
  // The account is NOT marked org-side here. The token is unvalidated at this
  // point — expiry, status and recipient-email are only checked by the
  // authenticated accept_org_invitation RPC. Org-side status is derived from
  // the current organization_members row that a successful acceptance creates
  // (#786), so nothing is written for it at signup; /invite and /auth/callback
  // are already exempt from the onboarding wizard, so nothing flashes in the
  // meantime.
  const hasMemberInvite = Boolean(
    readMemberInviteToken(typeof window !== "undefined" ? window.location.search : "")
  );

  // Sync mode with defaultMode whenever the modal is opened
  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      setMode(allowSignup ? defaultMode : "signin");
      setEmail("");
      setPassword("");
      setFullName("");
      setOrganizationName("");
      setAccountType("agent");
      setResetEmailSent(false);
      setConfirmationSentTo(null);
      setConfirmationIsInvite(false);
      setConfirmationOrgName(null);
      setAuthMethod("email");
      setOrgInvite(null);
      setOrgInviteError(null);
      setInlineError(null);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, defaultMode, allowSignup]);

  // Validate a closed-beta org-admin invite whenever the modal opens. The token
  // is read from the bootstrap's memory, never from the URL — the bootstrap has
  // already scrubbed it. A valid token forces signup mode + organization account;
  // the token itself is redeemed later via claim_org_admin_role(p_token) after
  // authentication.
  useEffect(() => {
    if (!isOpen) return;
    const token = readCapturedOrgInvite();
    if (!token) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_org_invite", { p_token: token });
      if (cancelled) return;
      // Minimal preview: no email, no stable org_id/slug. A valid token carries
      // display-only fields + is_bound; a stale token carries status only.
      const invite = (data ?? null) as unknown as {
        status?: string;
        is_bound?: boolean;
        org_name?: string | null;
        org_logo_url?: string | null;
      } | null;
      const status = invite?.status;
      if (error || !status) {
        setOrgInviteError("We couldn't validate this invitation link. Please try again or request a new one.");
        return;
      }
      if (status === "valid") {
        setOrgInvite({
          token,
          orgName: invite?.org_name ?? null,
          isBound: Boolean(invite?.is_bound),
          logoUrl: invite?.org_logo_url ?? null,
        });
        setOrgInviteError(null);
        setMode("signup");
        setAccountType("organization");
        // Prefill the ORGANIZATION field. fullName stays empty: it is the
        // person's name, and the defect this replaces was seeding it with the
        // org (see lib/signupMetadata.ts).
        if (invite?.org_name) setOrganizationName(invite.org_name);
      } else if (status === "consumed") {
        setOrgInviteError("This invitation has already been used. If you already created your account, please sign in.");
      } else if (status === "expired") {
        setOrgInviteError("This invitation has expired. Please request a new one from your inviter.");
      } else {
        setOrgInviteError("This invitation link is invalid.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const marketing = appearance === "marketing";

  // An auth failure: inline in the marketing look, a toast otherwise. Either
  // way the runtime error.message comes first and the fallback only after it.
  const reportError = (description: string) => {
    if (marketing) {
      setInlineError(description);
      return;
    }
    toast({ title: "Error", description, variant: "destructive" });
  };

  // The inline error in the default-look views (sign-up, and any invitation
  // state) for a marketing caller.
  const inlineErrorAlert = inlineError && (
    <div
      role="alert"
      className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{inlineError}</span>
    </div>
  );

  const handleGoogleSignIn = async () => {
    setInlineError(null);
    setAuthMethod("google");
    setIsGoogleLoading(true);
    try {
      // Carry the org-admin invite token through the OAuth round trip so
      // AuthCallback can redeem it (claim_org_admin_role); Google supplies its
      // own verified email, so the token's server-side email check still binds.
      const orgInviteToken = orgInvite?.token || readCapturedOrgInvite() || undefined;
      // The member invite rides along the same way, as a separate field. Google
      // owns its redirect URL, so unlike the email-confirmation path there is
      // no query parameter to hang it on — without this the token was simply
      // lost, and "Continue with Google" is offered on the invite link itself.
      // AuthCallback routes it to /invite; it is never redeemed here.
      const memberInviteToken =
        readMemberInviteToken(window.location.search) ?? undefined;
      // A review invitation rides along the same way and for the same reason:
      // Google owns its redirect URL, so there is no query parameter to hang it
      // on. OAuth always returns to the same browser, and the intent blob
      // already has a ten-minute TTL and is consumed on use.
      localStorage.setItem(
        "compaire_oauth_intent",
        JSON.stringify({
          accountType,
          orgInviteToken,
          memberInviteToken,
          reviewToken: reviewToken ?? undefined,
          claimOrg: claim ?? undefined,
          ts: Date.now(),
        })
      );
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      reportError(error.message || "Failed to sign in with Google.");
      setIsGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        // Constant path plus an opaque token, never a supplied destination.
        redirectTo: reviewResetPasswordUrl(window.location.origin, reviewToken),
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast({
        title: "Check your email",
        description: "We've sent you a password reset link.",
      });
    } catch (error: any) {
      reportError(error.message || "Failed to send reset email.");
    } finally {
      setIsLoading(false);
    }
  };


  const [passwordTouched, setPasswordTouched] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    if (mode === "signup") {
      const pwError = getPasswordError(password);
      if (pwError) {
        setPasswordTouched(true);
        return;
      }
    }
    setIsLoading(true);

    try {
      if (mode === "signup") {
        // Org accounts are invite-only during the closed beta. The org admin
        // role is granted solely by redeeming a valid single-use invite token
        // (below, after auth) — account_type metadata no longer grants it.
        if (accountType === "organization" && !orgInvite) {
          toast({
            title: "Organization accounts are invite-only",
            description: "Please use the invitation link sent to your email to set up your organization.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // HTML `required` accepts whitespace, and handle_new_user would then
        // write an empty profile name.
        if (!fullName.trim()) {
          toast({
            title: "Please enter your name",
            description: "Your name appears on your profile and next to what you post.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // A legacy (unbound) org invite seeds /admin/setup from this value, so an
        // all-whitespace entry would name the new organization nothing. A bound
        // invitation's org already exists and the field is read-only.
        if (accountType === "organization" && !orgInvite?.isBound && !organizationName.trim()) {
          toast({
            title: "Please enter your organization's name",
            description: "It becomes the name of the organization you're about to set up.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // The invited email is no longer disclosed by get_org_invite, so the
        // recipient types it themselves; the server-side claim validates the
        // match. Always use the typed address.
        const signupEmail = email;

        // Carry a member invitation (?invite_token=) through the confirmation
        // round trip. With email confirmation on, this callback URL is the only
        // place the token can resurface, and AuthCallback hands it to /invite.
        const memberInviteToken = readMemberInviteToken(window.location.search);

        const { data: signUpData, error } = await supabase.auth.signUp({
          email: signupEmail,
          password,
          options: {
            // A review recipient's URL never carries a member invitation, so
            // these are mutually exclusive in practice; the member invitation
            // keeps precedence if both were ever somehow present.
            // A listing claim (never alongside the other two) rides the same
            // constant callback path the same way.
            emailRedirectTo: memberInviteToken
              ? signupEmailRedirectUrl(window.location.origin, memberInviteToken)
              : claim && !reviewToken
                ? claimSignupRedirectUrl(window.location.origin, claim)
                : reviewSignupRedirectUrl(window.location.origin, reviewToken),
            // No org-side account_type even for a member invite: the token is
            // unvalidated at signup, and org-side status comes from the
            // membership accept_org_invitation creates (#786). With the
            // selector hidden, accountType stays the default "agent", which is
            // the correct resolution if the invite turns out to be invalid.
            // full_name is always the person; organization_name only for a
            // legacy org invite — see lib/signupMetadata.ts.
            data: buildSignupMetadata({
              accountType,
              fullName,
              organizationName,
              isBoundInvite: Boolean(orgInvite?.isBound),
            }),
          },
        });

        if (error) throw error;

        const continuation = resolveSignupContinuation({
          hasSession: Boolean(signUpData.session),
          isOrgAdminInvite: Boolean(orgInvite),
          search: window.location.search,
        });

        // Email confirmation is required: signUp created the account but no
        // session. The previous code immediately called signInWithPassword
        // here, which Supabase rejects with email_not_confirmed, and then told
        // the user to "sign in with your new credentials" — advice that could
        // not work. Nothing is redeemed on this path; the invitation waits for
        // the confirmation link, which now carries the token.
        if (continuation.kind === "confirm_email") {
          setConfirmationSentTo(signupEmail);
          setConfirmationIsInvite(continuation.hasMemberInvite);
          setConfirmationOrgName(
            continuation.hasOrgInvite ? orgInvite?.orgName || "your organization" : null,
          );
          resetForm();
          return;
        }

        // Grant the org admin role ONLY by redeeming the single-use, email-bound
        // invite token — one call, after auth. The RPC consumes the token in the
        // same transaction, so a second attempt is rejected as consumed.
        if (continuation.kind === "claim_org_admin") {
          // A Postgres RAISE (email_mismatch, expired, …) surfaces on the
          // resolved `error`, not as a throw, so it must be inspected here.
          // Only promote on a clean redemption; a failure gets a specific,
          // actionable message instead of silently landing the user as an agent.
          const { data: claimData, error: claimError } = await supabase.rpc("claim_org_admin_role", {
            p_token: orgInvite.token,
          });
          if (claimError) {
            console.error("Failed to redeem org admin invite:", claimError.message);
            const claimMessage = orgAdminClaimErrorMessage(claimError.message);
            toast({
              title: claimMessage.title,
              description: claimMessage.description,
              variant: "destructive",
            });
            resetForm();
            onClose();
            navigate("/explore");
            return;
          }
          markPromotedToAdmin(queryClient);
          clearCapturedOrgInvite();
          // A bound (pre-activation) claim attaches the recipient to an existing
          // org, so they go straight to the admin home — the profile editor — to
          // review it; a legacy invite has no org yet, so they continue to
          // /admin/setup to create one. The claim result's org_id is the source
          // of truth (the preview no longer carries a stable id).
          const boundOrgId = (claimData as { org_id?: string | null } | null)?.org_id ?? null;
          toast({
            title: "Welcome to Compaire!",
            description: boundOrgId ? "Let's review your organization." : "Let's set up your organization.",
          });
          resetForm();
          onClose();
          navigate(boundOrgId ? ADMIN_HOME : "/admin/setup");
          return;
        }

        // A member invitation is handed to /invite, which is the single owner of
        // calling accept_org_invitation. AuthModal used to redeem it here too,
        // so a successful acceptance could be followed by AcceptInvitePage's own
        // attempt reporting "Invitation not found or expired" on the consumed
        // token. Authenticate, then route — never redeem.
        if (continuation.kind === "accept_member_invite") {
          toast({
            title: "Welcome to Compaire!",
            description: "Finishing up your invitation…",
          });
          resetForm();
          onClose();
          navigate(continuation.path, { replace: true });
          return;
        }

        toast({
          title: "Welcome to Compaire!",
          description: "Your account is ready.",
        });
        resetForm();
        // A caller that must keep the visitor on its own page runs instead of
        // the role destination. Nothing above this point is affected: the
        // org-admin and member-invite continuations already returned.
        if (onAuthenticated) {
          onAuthenticated();
          return;
        }
        onClose();
        navigate("/explore");
      } else {
        const signinEmail = email;
        const { data: signInData, error } = await supabase.auth.signInWithPassword({
          email: signinEmail,
          password,
        });

        if (error) throw error;

        // Recovery path: an invited founder who already has an account signs in
        // via their invite link. Admin is granted only by redeeming the
        // single-use token, never by account_type metadata.
        const orgInviteToken = orgInvite?.token || readCapturedOrgInvite();
        if (orgInviteToken) {
          // Sign-in recovery is non-terminal: a bad invite must still leave the
          // user signed in, but the failure can no longer be silent — inspect
          // the resolved error and tell them why the promotion didn't happen.
          const { data: claimData, error: claimError } = await supabase.rpc("claim_org_admin_role", {
            p_token: orgInviteToken,
          });
          if (claimError) {
            console.error("Failed to redeem org admin invite on sign-in:", claimError.message);
            const claimMessage = orgAdminClaimErrorMessage(claimError.message);
            toast({
              title: claimMessage.title,
              description: claimMessage.description,
              variant: "destructive",
            });
          } else {
            markPromotedToAdmin(queryClient);
            clearCapturedOrgInvite();
            // A bound (pre-activation) claim attached this account to an existing
            // org, so route straight to the admin home (the profile editor) to
            // review it rather than falling through to the generic role destination.
            const boundOrgId = (claimData as { org_id?: string | null } | null)?.org_id ?? null;
            if (boundOrgId) {
              toast({ title: "Welcome back!", description: "Let's review your organization." });
              onClose();
              navigate(ADMIN_HOME, { replace: true });
              return;
            }
          }
        }

        // Recovery path for the member invite: a recipient who already has a
        // Compaire account clicks ?invite_token= and signs in rather than
        // signing up. The session now exists, so hand off to /invite — the one
        // surface that redeems the token — instead of role-based routing.
        const invitePath = pendingInviteRedirect(window.location.search);
        if (invitePath) {
          toast({
            title: "Welcome back!",
            description: "Finishing up your invitation…",
          });
          onClose();
          navigate(invitePath, { replace: true });
          return;
        }

        // A caller that must keep the visitor on its own page runs instead of
        // the role destination — and before resolving it, since that resolution
        // is a round trip whose only purpose is to pick a destination we are
        // not going to use.
        if (onAuthenticated) {
          toast({
            title: "Welcome back!",
            description: "You've successfully signed in.",
          });
          onAuthenticated();
          return;
        }

        // Fetch role (staff first, then admin/agent) and redirect accordingly
        const userId = signInData.user?.id;
        let redirectPath = "/feed";
        if (userId) {
          redirectPath = await resolvePostLoginDestination(userId, { agentFallback: "/dashboard" });
        }

        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });
        onClose();
        navigate(redirectPath);
      }
    } catch (error: any) {
      reportError(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setFullName("");
    // Invite-aware: switching to Sign In and back must not drop the account type
    // the invitation binds, which would hide the Organization Name field and lose
    // the legacy prefill. The modal-open effect and the chooser buttons keep their
    // plain resets — the open effect runs before the invite effect, and the
    // chooser is hidden whenever orgInvite is set.
    setAccountType(orgInvite ? "organization" : "agent");
    setOrganizationName(orgInvite?.orgName ?? "");
    setResetEmailSent(false);
    setPasswordTouched(false);
  };

  const switchMode = (newMode: "signin" | "signup" | "forgot") => {
    if (newMode === "signup" && !allowSignup) return;
    setMode(newMode);
    setInlineError(null);
    resetForm();
  };

  // Email-confirmation View — signUp succeeded but returned no session, so the
  // account is real and pending confirmation. The invitation (when there is
  // one) is redeemed after the recipient follows the link, not here.
  if (confirmationSentTo) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Check your email
            </DialogTitle>
            <DialogDescription className="text-center">
              Confirm your account to finish
              {confirmationOrgName
                ? ` setting up ${confirmationOrgName}`
                : confirmationIsInvite
                  ? " joining the organization"
                  : " signing up"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <Mail className="h-12 w-12 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-green-800">
                We've sent a confirmation link to <strong>{confirmationSentTo}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                Check your spam folder if you don't see it
              </p>
            </div>
            {confirmationOrgName ? (
              <p className="text-xs text-muted-foreground text-center">
                Opening that link confirms your account. Then open your invitation
                link again to finish setting up {confirmationOrgName} — it stays
                valid until it expires.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground text-center">
                Opening that link signs you in and
                {confirmationIsInvite
                  ? " completes your invitation."
                  : " finishes setting up your account."}{" "}
                You don't need to sign in before then.
              </p>
            )}
            <Button type="button" variant="outline" className="w-full" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Marketing look for the views a marketing visitor can reach: sign in, forgot
  // password and the reset-sent confirmation. Any invitation state (which can
  // force sign-up) falls through to the default views below.
  if (marketing && mode !== "signup" && !orgInvite && !orgInviteError) {
    const busy = isLoading || isGoogleLoading;
    const alert = inlineError && (
      <div role="alert" className="mk-alert">
        <AlertCircle size={18} aria-hidden="true" />
        <span>{inlineError}</span>
      </div>
    );
    return (
      <>
        <MarketingDialog
          open={isOpen}
          onOpenChange={(next) => !next && onClose()}
          title={mode === "forgot" ? "Reset Password" : "Sign in"}
          size="narrow"
        >
          {mode === "forgot" && resetEmailSent ? (
            <div role="status" className="mk-card mk-success" style={{ alignItems: "stretch" }}>
              <span className="mk-badge-circle" aria-hidden="true">
                <Mail size={20} />
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, textAlign: "center" }}>
                <p style={{ font: "600 17px/1.4 var(--mk-sans)" }}>Check your inbox for the reset link</p>
                <p className="mk-success__body" style={{ fontSize: 15, lineHeight: 1.5 }}>
                  We've sent a password reset link to{" "}
                  <strong style={{ fontWeight: 600, color: "var(--mk-ink)" }}>{email}</strong>
                </p>
                <p style={{ font: "400 14px/1.5 var(--mk-sans)", color: "var(--mk-muted)" }}>
                  Check your spam folder if you don't see it
                </p>
              </div>
              <button
                type="button"
                className="mk-textlink"
                style={{ alignSelf: "flex-start" }}
                onClick={() => switchMode("signin")}
              >
                Back to Sign In
              </button>
            </div>
          ) : mode === "forgot" ? (
            <form onSubmit={handleForgotPassword} className="mk-card mk-form" aria-busy={isLoading}>
              <p style={{ font: "400 15px/1.5 var(--mk-sans)", color: "var(--mk-ink-2)" }}>
                Enter your email and we'll send you a reset link
              </p>
              <div className="mk-field">
                <label htmlFor="resetEmail" className="mk-field__label">
                  Email
                </label>
                <input
                  id="resetEmail"
                  className="mk-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              {alert}
              <button
                type="submit"
                className="mk-btn mk-btn--primary mk-btn--lg mk-btn--block"
                disabled={isLoading}
                aria-busy={isLoading}
              >
                {isLoading && <span className="mk-spinner" aria-hidden="true" />}
                {isLoading ? "Sending..." : "Send Reset Link"}
              </button>
              <button
                type="button"
                className="mk-textlink"
                style={{ alignSelf: "flex-start" }}
                onClick={() => switchMode("signin")}
              >
                Back to Sign In
              </button>
            </form>
          ) : (
            <div className="mk-card mk-form">
              <button
                type="button"
                className="mk-btn mk-btn--line mk-btn--block"
                onClick={handleGoogleSignIn}
                disabled={busy}
                aria-busy={isGoogleLoading}
              >
                {isGoogleLoading ? (
                  <span className="mk-spinner mk-spinner--ink" aria-hidden="true" />
                ) : (
                  <MarketingGoogleIcon />
                )}
                Continue with Google
              </button>
              <div className="mk-divider" aria-hidden="true">
                or
              </div>
              <form onSubmit={handleSubmit} className="mk-stack" style={{ gap: 18 }} aria-busy={isLoading}>
                <div className="mk-field">
                  <label htmlFor="email" className="mk-field__label">
                    Email
                  </label>
                  <input
                    id="email"
                    className="mk-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={busy}
                    required
                  />
                </div>
                <div className="mk-field">
                  <div className="mk-field__row">
                    <label htmlFor="password" className="mk-field__label">
                      Password
                    </label>
                    <button type="button" className="mk-textlink" onClick={() => switchMode("forgot")}>
                      Forgot password?
                    </button>
                  </div>
                  <div className="mk-input-wrap">
                    <input
                      id="password"
                      className="mk-input"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={busy}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="mk-input-wrap__btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                    </button>
                  </div>
                </div>
                {alert}
                <button
                  type="submit"
                  className="mk-btn mk-btn--primary mk-btn--lg mk-btn--block"
                  disabled={busy}
                  aria-busy={isLoading}
                >
                  {isLoading && <span className="mk-spinner" aria-hidden="true" />}
                  Sign in
                </button>
              </form>
            </div>
          )}
          {/* The same rule as the default look's footer: sign-up when the
              caller allows it, otherwise the waitlist. */}
          {allowSignup ? (
            <div className="mk-auth-foot">
              <span>Don't have an account?</span>
              <button type="button" className="mk-textlink" onClick={() => switchMode("signup")}>
                Sign Up
              </button>
            </div>
          ) : (
            <div className="mk-auth-foot">
              <span>Don't have access yet?</span>
              <button
                type="button"
                className="mk-textlink"
                onClick={() => {
                  onClose();
                  setWaitlistOpen(true);
                }}
              >
                Join the waitlist
              </button>
            </div>
          )}
        </MarketingDialog>
        {waitlistOpen && (
          <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} appearance="marketing" />
        )}
      </>
    );
  }

  // Forgot Password View
  if (mode === "forgot") {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Reset password
            </DialogTitle>
            <DialogDescription className="text-center">
              {resetEmailSent
                ? "Check your inbox for the reset link"
                : "Enter your email and we'll send you a reset link"}
            </DialogDescription>
          </DialogHeader>

          {marketing && inlineErrorAlert}

          {resetEmailSent ? (
            <div className="space-y-4 mt-4">
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <Mail className="h-12 w-12 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-green-800">
                  We've sent a password reset link to <strong>{email}</strong>
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Check your spam folder if you don't see it
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => switchMode("signin")}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="resetEmail" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email address
                </Label>
                <Input
                  id="resetEmail"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => switchMode("signin")}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Sign In
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {mode === "signin" ? "Welcome Back" : "Create Your Account"}
          </DialogTitle>
          {mode === "signup" && signUpIntro && !orgInvite && !hasMemberInvite && (
            <DialogDescription className="text-center">{signUpIntro}</DialogDescription>
          )}
        </DialogHeader>

        {orgInvite && (
          <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary">
            You've been invited to set up{orgInvite.orgName ? ` ${orgInvite.orgName}` : " your organization"} on Compaire.
          </div>
        )}

        {orgInviteError && (
          <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {orgInviteError}
          </div>
        )}

        {/* A marketing caller's errors are reported inline (reportError), and
            sign-up always renders this view, so the alert must live here too:
            without it an email or Google rejection on sign-up was invisible. */}
        {marketing && inlineErrorAlert}

        {/* Member invitation (?invite_token=): the invitation already sets the
            organization and role, so the account-type step is skipped entirely
            rather than re-asking an answered question. */}
        {mode === "signup" && !orgInvite && hasMemberInvite && (
          <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary">
            You've been invited to join an organization on Compaire. Create your
            account to accept.
          </div>
        )}

        {/* Role Selector — hidden for an org-admin invite (which binds the
            account type) and for a member invitation (whose role the RPC
            resolves; the account-type question is not re-asked). */}
        {mode === "signup" && !orgInvite && !hasMemberInvite && !lockAccountType && (
          <div className="space-y-2 mt-4">
            <Label className="text-sm font-medium">I am signing up as</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setAccountType("agent"); setFullName(""); setOrganizationName(""); }}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                  accountType === "agent"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/50 text-muted-foreground"
                }`}
              >
                <Briefcase className="h-6 w-6" />
                <span className="text-sm font-medium">Agent</span>
                <span className="text-[11px] text-muted-foreground text-center leading-tight">Explore teams &amp; opportunities</span>
              </button>
              <button
                type="button"
                onClick={() => { setAccountType("organization"); setFullName(""); setOrganizationName(""); }}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                  accountType === "organization"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/50 text-muted-foreground"
                }`}
              >
                <Building2 className="h-6 w-6" />
                <span className="text-sm font-medium">Organization</span>
                <span className="text-[11px] text-muted-foreground text-center leading-tight">Recruit &amp; manage your team</span>
              </button>
            </div>
          </div>
        )}

        {/* Google Sign In Button */}
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 font-medium"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
          >
            {isGoogleLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <GoogleIcon />
            )}
            <span className="ml-2">Continue with Google</span>
          </Button>
        </div>

        {/* Email auth fields — hidden when Google method is active */}
        {authMethod === "email" && (
          <>
            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with email
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "signup" && (
                <SignupNameFields
                  accountType={accountType}
                  isBoundInvite={Boolean(orgInvite?.isBound)}
                  fullName={fullName}
                  organizationName={organizationName}
                  onFullNameChange={setFullName}
                  onOrganizationNameChange={setOrganizationName}
                />
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {orgInvite && (
                  <p className="text-xs text-muted-foreground">
                    Use the email address this invitation was sent to.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    Password
                  </Label>
                  {mode === "signin" && (
                    <button
                      type="button"
                      onClick={() => switchMode("forgot")}
                      className="text-xs text-muted-foreground hover:text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={mode === "signup" ? "Create a password" : "Enter your password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPasswordTouched(true); }}
                    required
                    minLength={mode === "signup" ? 8 : 6}
                    className="pr-10"
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {mode === "signup" && <PasswordStrengthMeter password={password} />}
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {mode === "signin" ? "Signing In..." : "Creating Account..."}
                  </>
                ) : (
                  mode === "signin" ? "Sign In" : "Create Account"
                )}
              </Button>
            </form>
          </>
        )}

        <div className="text-center text-sm text-muted-foreground mt-4">
          {mode === "signin" ? (
            <>
              Don't have an account?{" "}
              {allowSignup ? (
                <button
                  type="button"
                  onClick={() => switchMode("signup")}
                  className="text-primary font-medium hover:underline"
                >
                  Sign Up
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    setWaitlistOpen(true);
                  }}
                  className="text-primary font-medium hover:underline"
                >
                  Join the waitlist
                </button>
              )}
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className="text-brand-navy font-medium hover:underline"
              >
                Sign In
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
    {/* Sibling, not a child: AuthModal closes itself before opening this, and
        the component stays mounted while isOpen is false. */}
    {waitlistOpen && <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} />}
    </>
  );
};

export default AuthModal;
